import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * JavaScript tampering / "prototype lies" detection.
 *
 * Technique: an anti-detect browser or stealth script replaces native
 * functions (navigator.webdriver, HTMLCanvasElement.toDataURL, permissions.query,
 * …) with JavaScript shims. A shim is functionally almost perfect but leaks in
 * three ways we can test:
 *
 *  1. Function.prototype.toString no longer returns "function X() { [native code] }".
 *     A shim's source is its real JS.
 *  2. A native function has no own `prototype`/`arguments`/`caller` properties and
 *     its own-property names are exactly ["length","name"]. A shim defined as a
 *     function/arrow/async has different own properties.
 *  3. A native function obeys engine-specific error contracts — calling a
 *     constructor without `new` on a class throws a TypeError whose *stack shape*
 *     differs when the function is a Proxy. The error message text is also
 *     engine-specific, which is what gives us engine fingerprinting for free.
 *
 * We deliberately do NOT probe a huge API surface: the reference implementation
 * tests dozens of prototypes, which is slow and noisy. We test the handful that
 * anti-detect tooling actually replaces most often, plus Function.prototype.toString
 * itself (the linchpin of every other check).
 *
 * Credit: the approach follows CreepJS (MIT), specifically
 * src/lies/index.ts. The technique predates it (see Nikiforakis, "Detecting
 * Privacy Badger's Canvas FP detection" and Ad Tech Madness's JavaScript-tampering
 * write-ups). We implement the checks ourselves rather than copying their code.
 */

const TAMPERING_ARTICLE =
  'https://adtechmadness.wordpress.com/2019/03/23/javascript-tampering-detection-and-stealth/';
const NIKIFORAKIS = 'https://www.usenix.org/conference/usenixsecurity17/technical-sessions/presentation/nikiforakis';

/** Native toString shapes a conformant engine produces. */
const NATIVE_TOSTRINGS = [
  'function () { [native code] }',
  'function get () { [native code] }',
];

export const liesProbe: Probe = {
  id: 'lies',
  label: 'JavaScript tampering (prototype lies)',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Detects shimmed native functions via toString, own properties, and error contracts',
  sources: [TAMPERING_ARTICLE, NIKIFORAKIS],
  budgetMs: 3000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const props: Record<string, string[]> = {};
    let searched = 0;

    // --- 0. the linchpin: is Function.prototype.toString itself native? ------
    const toStringLie = liesFor(Function.prototype.toString);
    searched += 1;
    if (toStringLie.length) {
      props['Function.toString'] = toStringLie;
      findings.push({
        id: 'lies.function-tostring-shimmed',
        severity: 'high',
        category: 'browser',
        layer: 'browser',
        confidence: 'high',
        title: 'Function.prototype.toString is not a native function',
        detail:
          'Function.prototype.toString must stringify any native function to "function name() { [native code] }". If it is itself replaced, every shim on the page becomes invisible to the standard detection trick, which means a stealth tool is deliberately hiding its modifications. This is the most consequential single check in this probe.',
        sources: [TAMPERING_ARTICLE],
        evidence: toStringLie,
        scoreContribution: 40,
      });
    }

    // --- 1. commonly-shimmed APIs ------------------------------------------
    // Each entry: how to reach the object holding the functions, and which
    // members anti-detect tooling most often replaces.
    const targets: Array<{ label: string; get: () => unknown; members?: string[] }> = [
      { label: 'Navigator', get: () => (globalThis as { Navigator?: unknown }).Navigator, members: ['webdriver', 'userAgent', 'platform', 'plugins', 'languages', 'hardwareConcurrency'] },
      { label: 'HTMLCanvasElement', get: () => (globalThis as { HTMLCanvasElement?: unknown }).HTMLCanvasElement, members: ['toDataURL', 'getContext'] },
      { label: 'Permissions', get: () => (globalThis as { Permissions?: unknown }).Permissions, members: ['query'] },
      { label: 'WebGLRenderingContext', get: () => (globalThis as { WebGLRenderingContext?: unknown }).WebGLRenderingContext, members: ['getParameter', 'readPixels'] },
      { label: 'Intl.DateTimeFormat', get: () => (globalThis as { Intl?: { DateTimeFormat?: unknown } }).Intl?.DateTimeFormat, members: ['resolvedOptions', 'format'] },
      { label: 'Screen', get: () => (globalThis as { Screen?: unknown }).Screen, members: ['availWidth', 'availHeight', 'colorDepth'] },
      { label: 'MediaDevices', get: () => (globalThis as { MediaDevices?: unknown }).MediaDevices, members: ['enumerateDevices', 'getUserMedia'] },
    ];

    for (const t of targets) {
      let ctor: Record<string, unknown> | null = null;
      try {
        const raw = t.get();
        ctor = (raw ?? null) as Record<string, unknown> | null;
      } catch {
        ctor = null;
      }
      if (!ctor) continue;

      const proto = (ctor.prototype ?? ctor) as Record<string, unknown>;
      if (!proto) continue;
      // If no explicit members, check a handful of own function properties.
      const members = t.members ?? Object.getOwnPropertyNames(proto).filter((n) => {
        if (n === 'constructor' || n === 'length' || n === 'name') return false;
        try {
          return typeof proto[n] === 'function';
        } catch {
          return false;
        }
      }).slice(0, 8);

      for (const name of members) {
        let fn: unknown;
        try {
          fn = proto[name];
        } catch {
          continue;
        }
        if (typeof fn !== 'function') continue;
        searched += 1;
        const lies = liesFor(fn as (...a: unknown[]) => unknown);
        if (lies.length) props[`${t.label}.${name}`] = lies;
      }
    }

    const lieList = Object.keys(props).sort();
    const lieCount = Object.values(props).reduce((a, v) => a + v.length, 0);

    // One finding for "there are shimmed APIs" beyond the toString case.
    if (lieList.length > 0) {
      const nonTostring = lieList.filter((k) => k !== 'Function.toString');
      if (nonTostring.length > 0) {
        findings.push({
          id: 'lies.shimmed-apis',
          severity: 'high',
          category: 'browser',
          layer: 'browser',
          confidence: 'high',
          title: `Shimmed native function(s) detected: ${nonTostring.join(', ')}`,
          detail:
            'These properties are not native engine functions. A real browser always returns [native code] for built-in functions and gives them exactly the own properties "length" and "name". Anti-detect browsers and stealth automation scripts replace properties like navigator.webdriver, canvas toDataURL, or permissions.query with JavaScript shims; those shims are functionally correct but structurally detectable, because a JS function has a real source string and different own properties than a native one.',
          sources: [TAMPERING_ARTICLE, NIKIFORAKIS],
          evidence: nonTostring.map((k) => `${k}: ${props[k].join(', ')}`),
          scoreContribution: Math.min(30, 10 + nonTostring.length * 5),
        });
      }
    }

    return {
      signals: {
        apiPropertiesSearched: ok(searched, 'derived'),
        lieCount: ok(lieCount, 'derived'),
        lieList: ok(lieList, 'client'),
        liesByApi: ok(props, 'client'),
        // A convenience bitmask matching the reference tool's "bitmask" idea.
        clean: ok(lieCount === 0, 'derived'),
      },
      findings,
      parts: {
        browserFeatures: {
          // A shimmed stack is a stable, high-entropy property of the environment.
          lieCountBucket: lieCount === 0 ? 'clean' : lieCount <= 3 ? 'light' : 'heavy',
          lieApiCount: lieList.length,
        },
      },
    };
  },
};

/**
 * Run every lie check against one function and return the list of failures.
 * A conformant native function passes all of these; a JS shim fails several.
 */
function liesFor(fn: (...a: unknown[]) => unknown): string[] {
  const lies: string[] = [];
  const FTS = Function.prototype.toString;

  // 1. toString shape.
  let src = '';
  try {
    src = FTS.call(fn);
  } catch {
    lies.push('failed toString call');
  }
  if (src && !NATIVE_TOSTRINGS.some((s) => src === s) && !/^\s*function\s*\w*\s*\(\)\s*\{\s*\[native code\]\s*\}\s*$/.test(src) && !/get\s+\w*\s*\(\)\s*\{\s*\[native code\]\s*\}/.test(src)) {
    lies.push('failed toString');
  }

  // 2. Own properties: a native function has exactly length + name.
  let ownNames: string[] = [];
  try {
    ownNames = Object.getOwnPropertyNames(fn).sort();
  } catch {
    lies.push('failed own property names');
  }
  if (ownNames.length && ownNames.join(',') !== 'length,name') {
    lies.push('failed own property names');
  }

  // 3. Accessing .arguments/.caller on a native (strict) function throws a
  //    TypeError; on a shim it usually returns undefined or a value.
  //    Assigning to a sink keeps this an expression statement the linter and
  //    the JS engine both accept, without changing the semantics.
  let argThrew = false;
  try {
    const sink = fn as unknown as Record<string, unknown>;
    void sink.arguments;
  } catch {
    argThrew = true;
  }
  let callerThrew = false;
  try {
    const sink = fn as unknown as Record<string, unknown>;
    void sink.caller;
  } catch {
    callerThrew = true;
  }
  // Native functions that expose arguments/caller are "sloppy" functions; most
  // built-ins are strict and throw. A shim (arrow/async/bound) returns undefined
  // for both instead of throwing.
  if (!argThrew && !callerThrew && /function\s*\(\)/.test(src)) {
    lies.push('failed arguments/caller strictness');
  }

  // 4. 'prototype' in fn: true for a normal function declaration, false for
  //    methods/natives/arrows. A shim written as `function(){}` has a prototype;
  //    most natives do not. Combined with #2 this is quite decisive.
  let hasProto = false;
  try {
    hasProto = 'prototype' in (fn as object);
  } catch {
    /* ignore */
  }
  if (hasProto && ownNames.indexOf('prototype') === -1) {
    lies.push('failed prototype presence');
  }

  return lies;
}

registerProbe(liesProbe);
